// The bridge to the iOS app shell (mobile-apps.md §5, R2927, R2936).
//
// The page sends an op through the WKWebView message handler
// `window.webkit.messageHandlers.ap`, and the app shell answers with the
// CustomEvent `ap-shell` on the window, whose `detail` carries `op` and
// the answer fields. `gen/mobile.ts` holds every name.
//
// The page sends an op only when `apShell.bridge` is at least the op's
// "since" version (`bridgeHas`), so an old app shell never receives an op
// that it does not know. A browser has no `apShell`, and every op is
// absent there.

import { SHELL_EVENT, SHELL_GLOBAL, SHELL_HANDLER, SHELL_OPS, type ShellOp } from '../gen/mobile';
import { num } from '../net/json';

/** The answer ops of the app shell (mobile-apps.md §5). An answer op is
 *  no op the page sends, so `SHELL_OPS` does not list it. */
export const SHELL_ANSWER = {
  /** The APNs token of a granted permission: `{token, env}`. */
  pushToken: 'push.token',
  /** The person refused the push permission. */
  pushDenied: 'push.denied',
  /** The permission and the token, without a prompt: `{granted, token?, env?}`. */
  pushState: 'push.state',
} as const;

/** One answer of the app shell: `op` and the answer fields. */
export interface ShellAnswer {
  op: string;
  [field: string]: unknown;
}

/** The parts of the window that the bridge reads. A test passes a fake. */
export interface BridgeWindow {
  readonly [SHELL_GLOBAL]?: { readonly bridge?: unknown };
  readonly webkit?: {
    readonly messageHandlers?: Readonly<Record<string, { postMessage(message: unknown): void } | undefined>>;
  };
  addEventListener(type: string, fn: (ev: Event) => void): void;
  removeEventListener(type: string, fn: (ev: Event) => void): void;
}

/** The window of the page, or null where there is none. */
export function pageWindow(): BridgeWindow | null {
  return typeof window === 'undefined' ? null : window as unknown as BridgeWindow;
}

/** The bridge version the app shell states, or 0 for a browser. */
export function bridgeVersion(w: BridgeWindow | null = pageWindow()): number {
  try {
    return num(w?.[SHELL_GLOBAL]?.bridge);
  } catch {
    return 0;
  }
}

/** True when the app shell knows `op`: its bridge is at least the op's
 *  "since" version. */
export function bridgeHas(op: ShellOp, w: BridgeWindow | null = pageWindow()): boolean {
  return bridgeVersion(w) >= SHELL_OPS[op];
}

/**
 * Sends one op with its payload. It answers false when the app shell does
 * not know the op, has no handler, or the post throws. The payload cannot
 * replace the op name.
 */
export function bridgeSend(op: ShellOp, payload: Readonly<Record<string, unknown>> = {},
  w: BridgeWindow | null = pageWindow()): boolean {
  if (!bridgeHas(op, w)) return false;
  try {
    const handler = w?.webkit?.messageHandlers?.[SHELL_HANDLER];
    if (!handler) return false;
    handler.postMessage({ ...payload, op });
    return true;
  } catch {
    return false;
  }
}

/** True for an event detail that is an answer of the app shell. */
function isAnswer(d: unknown): d is ShellAnswer {
  return typeof d === 'object' && d !== null && typeof (d as { op?: unknown }).op === 'string';
}

/** Calls `fn` for each answer whose op is `op`. It answers the function
 *  that stops the listening. */
export function bridgeOn(op: string, fn: (answer: ShellAnswer) => void,
  w: BridgeWindow | null = pageWindow()): () => void {
  if (!w) return () => {};
  const listener = (ev: Event): void => {
    const d = (ev as CustomEvent<unknown>).detail;
    if (isAnswer(d) && d.op === op) fn(d);
  };
  w.addEventListener(SHELL_EVENT, listener);
  return () => w.removeEventListener(SHELL_EVENT, listener);
}

/**
 * Sends `op` and waits for the first answer whose op is one of `answers`.
 * It resolves to null at once when the op cannot go. It sets no time
 * limit: an op that a person answers, such as the push prompt, takes as
 * long as the person takes.
 */
export function bridgeAsk(op: ShellOp, answers: readonly string[], payload: Readonly<Record<string, unknown>> = {},
  w: BridgeWindow | null = pageWindow()): Promise<ShellAnswer | null> {
  if (!bridgeHas(op, w)) return Promise.resolve(null);
  return new Promise((done) => {
    let offs: (() => void)[] = [];
    const stop = (): void => {
      for (const off of offs) off();
      offs = [];
    };
    offs = answers.map((a) => bridgeOn(a, (answer) => {
      stop();
      done(answer);
    }, w));
    if (!bridgeSend(op, payload, w)) {
      stop();
      done(null);
    }
  });
}
